import { Capacitor, registerPlugin } from '@capacitor/core';

// --- PLUGIN INTERFACES ---

export interface DeviceIdPlugin {
  getDeviceId(): Promise<{ deviceId: string; platform: string }>;
}

export interface PrinterDiscoveryPlugin {
  startDiscovery(): Promise<{ success: boolean; message: string }>;
  stopDiscovery(): Promise<{ success: boolean; message: string }>;
  getDiscoveredPrinters(): Promise<{ printers: DiscoveredPrinter[]; count: number }>;
}

export interface DiscoveredPrinter {
  name: string;
  address: string;
  type: 'bluetooth' | 'usb';
  rssi?: number;
}

export interface PrinterConnectionPlugin {
  connectUSB(options?: { driverType?: string }): Promise<{ success: boolean; message: string; type: 'usb' }>;
  connectBluetooth(options: { deviceAddress: string; driverType?: string }): Promise<{ success: boolean; message: string; type: 'bluetooth' }>;
  connectWiFi(options: { ipAddress: string; port?: string; driverType?: string }): Promise<{ success: boolean; message: string; type: 'wifi' }>;
  disconnect(): Promise<{ success: boolean; message: string }>;
  getConnectionStatus(): Promise<{ connected: boolean; type?: string; driverType?: string }>;
}

export interface DeviceIdPlugin {
  getDeviceId(): Promise<{ deviceId: string; platform: string }>;
}

export interface PrinterPrintPlugin {
  printPDF(options: {
    base64Pdf: string;
    printerLanguage?: 'TSPL' | 'CPCL' | 'ESC';
    driverType?: 'android_tspl' | 'godex' | 'tsc' | 'citizen';
    godexSpeed?: number;
    godexDarkness?: number;
    godexPdfMode?: 'sdk' | 'gepl' | 'legacy_bitmap' | 'ezpl_hex';
    width?: number;
    height?: number;
    paperType?: 'label' | 'continuous' | 'black_label';
    gapEnabled?: boolean;
    compress?: boolean;
    cutAfterPrint?: boolean;
    dpi?: number;
    rotate?: boolean;
    feedEnabled?: boolean;
    feedAmount?: number;
  }): Promise<{ success: boolean; message: string }>;
  sendRawCommand(options: {
    command: string;
    driverType?: 'android_tspl' | 'godex' | 'tsc' | 'citizen';
  }): Promise<{ success: boolean; message: string }>;
  printImage(options: {
    base64Image: string;
    printerLanguage?: 'TSPL' | 'CPCL' | 'ESC';
    width?: number;
    height?: number;
    paperType?: 'label' | 'continuous' | 'black_label';
    gapEnabled?: boolean;
    compress?: boolean;
    cutAfterPrint?: boolean;
    dpi?: number;
    rotate?: boolean;
    mirror?: boolean;
    feedEnabled?: boolean;
    feedAmount?: number;
  }): Promise<{ success: boolean; message: string }>;
}

// --- OKI PRINTER PLUGIN INTERFACE ---

export interface OkiPrinterPlugin {
  discoverPrinters(): Promise<{ printers: any; count: number }>;
  connect(): Promise<{ success: boolean; message: string; deviceName?: string; productName?: string }>;
  connectNetwork(options: { ipAddress: string; port?: number }): Promise<{ success: boolean; message: string; connectionType?: string; ipAddress?: string }>;
  disconnect(): Promise<{ success: boolean; message: string }>;
  getStatus(): Promise<{ connected: boolean; deviceName?: string; productName?: string }>;
  printRawPdf(options: {
    base64Pdf: string;
    mediaSource?: string;
    mediaWeight?: string;
    mediaType?: string;
    paperSize?: string;
    customPaperWidth?: number;
    customPaperLength?: number;
    resolution?: number;
    economode?: boolean;
    density?: number;
    orientation?: string;
    xOffset?: number;
    yOffset?: number;
    leftMarginCompensation?: number;  // in mm, negative shifts left (default 0)
    customPjlHeader?: string;  // Custom PJL header (replaces auto-generated), use {WIDTH} and {HEIGHT} as placeholders
  }): Promise<{ success: boolean; message: string; bytesSent?: number; pjlHeader?: string; prnFile?: string }>;
  printRawBytes(options: { base64Data: string }): Promise<{ success: boolean; bytesSent?: number }>;
  testPrint(options: { mode: string }): Promise<{ success: boolean; message: string }>;
}

// --- CITIZEN PRINTER PLUGIN INTERFACE ---

export interface CitizenPrinterPlugin {
  connectUSB(): Promise<{ success: boolean; message: string; type: 'usb' }>;
  disconnect(): Promise<{ success: boolean; message: string }>;
  getConnectionStatus(): Promise<{ connected: boolean; type?: string; driverType?: string }>;
  printPdf(options: {
    pdfData: string;
    widthMm?: number;
    heightMm?: number;
    copies?: number;
    sensorType?: 'gap' | 'blackmark' | 'continuous';  // Label sensor type
    darkness?: number;   // -5 to +5, 0 = default
    speed?: number;      // 1-6 ips, 0 = default
    debug?: boolean;     // Enable debug logging
    autoCut?: boolean;   // Auto-cut after print (default true)
    printMethod?: 'direct' | 'transfer';  // direct = Direct Thermal, transfer = Thermal Transfer (ribbon)
    feedBefore?: number;  // Feed before print in mm (0-50) - for continuous media
    feedAfter?: number;   // Feed after print in mm (0-50) - for continuous media
    rollWidth?: number;   // Roll width in mm - for continuous media
  }): Promise<{ success: boolean; message: string; widthMm?: number; heightMm?: number; sensorType?: string; autoCut?: boolean; printMethod?: string; prnFile?: string }>;
  printTspl(options: { commands: string }): Promise<{ success: boolean; message: string }>;
  testPrint(options: { mode: 'feed' | 'text' | 'pattern' }): Promise<{ success: boolean; message: string; debugLog?: string }>;
}


// --- REGISTER PLUGINS ---

const PrinterDiscovery = registerPlugin<PrinterDiscoveryPlugin>('PrinterDiscovery');
const PrinterConnection = registerPlugin<PrinterConnectionPlugin>('PrinterConnection');
const PrinterPrint = registerPlugin<PrinterPrintPlugin>('PrinterPrint');
const OkiPrinter = registerPlugin<OkiPrinterPlugin>('OkiPrinter');

// Export CitizenPrinter for direct access (test print, etc.)
export const CitizenPrinter = registerPlugin<CitizenPrinterPlugin>('CitizenPrinter');

// --- BRIDGE UTILITIES ---

export const isAndroid = Capacitor.getPlatform() === 'android';
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isMobile = isAndroid || isIOS;
export const isElectron = /Electron/.test(navigator.userAgent);

export interface PrintOptions {
  silent?: boolean;
  printerName?: string;
  driverType?: 'android_tspl' | 'oki_usb' | 'godex' | 'tsc' | 'citizen' | 'meena_lite';
  godexSpeed?: number;
  godexDarkness?: number;
  godexPdfMode?: 'sdk' | 'gepl' | 'legacy_bitmap' | 'ezpl_hex';
  citizenDarkness?: number;  // -5 to +5, 0 = default
  citizenSpeed?: number;     // 1-6 ips, 0 = default
  citizenAutoCut?: boolean;  // Auto-cut after print (default true)
  citizenPrintMethod?: 'direct' | 'transfer';  // direct = Direct Thermal, transfer = Thermal Transfer
  citizenFeedBefore?: number;  // Feed before print in mm (0-50)
  citizenFeedAfter?: number;   // Feed after print in mm (0-50)
  citizenRollWidth?: number;   // Roll width in mm
  width?: number;
  height?: number;
  connectionType?: 'usb' | 'bluetooth' | 'wifi' | 'usb_oki';
  androidAddress?: string;
  androidPort?: string;
  printerLanguage?: 'TSPL' | 'CPCL' | 'ESC';
  base64Pdf?: string;
  base64Image?: string; // For fast path image printing
  paperType?: 'label' | 'continuous' | 'black_label';
  gapEnabled?: boolean;
  compress?: boolean;
  cutAfterPrint?: boolean;
  dpi?: number;
  rotate?: boolean;
  mirror?: boolean;
  feedEnabled?: boolean;
  feedAmount?: number;
  xOffset?: number;
  yOffset?: number;
}

// --- EXPORTED FUNCTIONS ---

export const getPrinters = async (): Promise<any[]> => {
  if (isElectron) {
    try {
      const { ipcRenderer } = (window as any).require('electron');
      return await ipcRenderer.invoke('get-printers');
    } catch (e) {
      console.error('Electron getPrinters failed', e);
      return [];
    }
  } else if (isMobile) {
    // On Android/iOS, we don't have "system printers", we discover them.
    // This method is mainly for the dropdown list, which might not apply to mobile in the same way.
    // But we can return previously discovered/cached ones if we wanted.
    return [];
  }
  return [];
};

export const startPrinterDiscovery = async (): Promise<void> => {
  if (isMobile) {
    await PrinterDiscovery.startDiscovery();
  }
};

export const stopPrinterDiscovery = async (): Promise<void> => {
  if (isMobile) {
    await PrinterDiscovery.stopDiscovery();
  }
};

export const getDiscoveredAndroidPrinters = async (): Promise<DiscoveredPrinter[]> => {
  if (isMobile) {
    const result = await PrinterDiscovery.getDiscoveredPrinters();
    return result.printers || [];
  }
  return [];
};

export const connectAndroidPrinter = async (device: DiscoveredPrinter & { driverType?: string }): Promise<boolean> => {
  if (!isMobile) return false;

  try {
    // OKI printers use their own plugin (OkiPrinterPlugin) — never route through thermal PrinterConnection
    if (device.driverType === 'oki_usb') {
      return true; // OKI connects internally via executePrintJob
    }

    // Citizen and Godex are Android-only (USB-only SDKs)
    if (isIOS && (device.driverType === 'citizen' || device.driverType === 'godex')) {
      throw new Error(`${device.driverType} printers are not supported on iOS`);
    }

    // USB is not supported on iOS
    if (isIOS && device.type === 'usb') {
      throw new Error('USB connection is not supported on iOS. Use Bluetooth or WiFi instead.');
    }

    // Use dedicated Citizen plugin for Citizen printers (handles USB permission properly)
    if (device.driverType === 'citizen') {
      if (device.type === 'usb') {
        await CitizenPrinter.connectUSB();
        return true;
      } else {
        throw new Error('Citizen printer only supports USB connection');
      }
    }

    // Use standard connection for other drivers
    if (device.type === 'usb') {
      await PrinterConnection.connectUSB({ driverType: device.driverType });
    } else if (device.type === 'bluetooth') {
      await PrinterConnection.connectBluetooth({ deviceAddress: device.address, driverType: device.driverType });
    } else if ((device.type as string) === 'wifi') {
      await PrinterConnection.connectWiFi({ ipAddress: device.address, driverType: device.driverType });
    }
    return true;
  } catch (e) {
    console.error('Connection failed', e);
    throw e;
  }
};

export const getAndroidConnectionStatus = async (): Promise<{ connected: boolean; type?: string; driverType?: string }> => {
  if (!isMobile) return { connected: false };
  return await PrinterConnection.getConnectionStatus();
};

export const sendAndroidRawCommand = async (options: { command: string; driverType?: 'android_tspl' | 'godex' | 'tsc' | 'citizen' }): Promise<{ success: boolean; message: string }> => {
  if (!isAndroid) {
    throw new Error('sendAndroidRawCommand is only supported on Android');
  }
  // TSC and Citizen use TSPL-compatible commands, map them to android_tspl
  const mappedDriver = (options.driverType === 'tsc' || options.driverType === 'citizen')
    ? 'android_tspl'
    : (options.driverType ?? 'godex');
  return await PrinterPrint.sendRawCommand({
    command: options.command,
    driverType: mappedDriver as 'android_tspl' | 'godex'
  });
};

export const printPlate = async (options: PrintOptions): Promise<any> => {
  if (isMobile) {
    try {
      if (!options.base64Pdf) {
        throw new Error("PDF data required for printing");
      }

      // Handle OKI Printing (network on iOS, USB+network on Android)
      if (options.connectionType === 'usb_oki' || options.driverType === 'oki_usb') {
        return await printToOki(options.base64Pdf, {
           mediaSource: 'AUTO',
           orientation: options.rotate ? 'LANDSCAPE' : 'PORTRAIT',
           xOffset: options.xOffset,
           yOffset: options.yOffset
        });
      }

      // Clean base64 if needed (remove data URI prefix)
      const base64Data = options.base64Pdf.includes(',')
        ? options.base64Pdf.split(',')[1]
        : options.base64Pdf;

      // Citizen and Godex are Android-only
      if (isIOS && (options.driverType === 'citizen' || options.driverType === 'godex')) {
        throw new Error(`${options.driverType} printers are not supported on iOS`);
      }

      // Handle Citizen Printing via dedicated plugin (Android only, uses SDK for proper USB handling)
      if (options.driverType === 'citizen') {
        // Auto-connect if not already connected
        const status = await CitizenPrinter.getConnectionStatus();
        if (!status.connected) {
          console.log('Citizen: Not connected, attempting auto-connect...');
          try {
            await CitizenPrinter.connectUSB();
            console.log('Citizen: Auto-connected successfully');
          } catch (connectError: any) {
            console.error('Citizen: Auto-connect failed:', connectError);
            throw new Error('Citizen printer not connected. Please connect via the printer settings first. Error: ' + (connectError?.message || 'Connection failed'));
          }
        }

        return await CitizenPrinter.printPdf({
          pdfData: base64Data,
          widthMm: options.width,
          heightMm: options.height,
          copies: 1,
          sensorType: (options.paperType === 'continuous' ? 'continuous' :
                       options.paperType === 'black_label' ? 'blackmark' : 'gap') as 'gap' | 'blackmark' | 'continuous',
          darkness: options.citizenDarkness,
          speed: options.citizenSpeed,
          autoCut: options.citizenAutoCut ?? true,
          printMethod: options.citizenPrintMethod || 'direct',
          feedBefore: options.citizenFeedBefore || 0,
          feedAfter: options.citizenFeedAfter || 0,
          rollWidth: options.citizenRollWidth || 80,
          debug: true  // Enable debug logging for now
        });
      }

      // Map TSC to android_tspl as it uses TSPL-compatible commands
      // Map TSC and Meena Lite to android_tspl as they use TSPL-compatible commands
      // On iOS, driverType is ignored by the native plugin (it uses AYSDK directly)
      const mappedDriver = (options.driverType === 'tsc' || options.driverType === 'meena_lite')
        ? 'android_tspl'
        : (options.driverType === 'godex' ? 'godex' : 'android_tspl');

      return await PrinterPrint.printPDF({
        base64Pdf: base64Data,
        printerLanguage: options.printerLanguage || 'TSPL',
        driverType: mappedDriver,
        godexSpeed: options.godexSpeed,
        godexDarkness: options.godexDarkness,
        godexPdfMode: options.godexPdfMode || 'sdk',
        width: options.width,
        height: options.height,
        paperType: options.paperType,
        gapEnabled: options.gapEnabled,
        compress: options.compress,
        cutAfterPrint: options.cutAfterPrint,
        dpi: options.dpi || 203, // Default to 203 DPI for thermal printers
        rotate: options.rotate || false,
        feedEnabled: options.feedEnabled || false,
        feedAmount: options.feedAmount || 5
      });
    } catch (e) {
      console.error('Print failed', e);
      throw e;
    }
  } else if (isElectron) {
    try {
      const { ipcRenderer } = (window as any).require('electron');
      return await ipcRenderer.invoke('print-plate', options);
    } catch (e) {
      console.error('Electron Print failed', e);
      throw e;
    }
  } else {
    return window.print();
  }
};

// Listeners
export const addDiscoveryListener = (eventName: string, callback: (data: any) => void) => {
    if (isMobile) {
        PrinterDiscovery.addListener(eventName as any, callback);
    }
};

export const removeAllDiscoveryListeners = () => {
    if (isMobile) {
        PrinterDiscovery.removeAllListeners();
    }
};

export const printImage = async (options: PrintOptions): Promise<any> => {
  if (isMobile) {
    try {
      if (!options.base64Image) {
        throw new Error("Image data required for printing");
      }

      const base64Data = options.base64Image.includes(',')
        ? options.base64Image.split(',')[1]
        : options.base64Image;

      return await PrinterPrint.printImage({
        base64Image: base64Data,
        printerLanguage: options.printerLanguage || 'TSPL',
        width: options.width,
        height: options.height,
        paperType: options.paperType,
        gapEnabled: options.gapEnabled,
        compress: options.compress,
        cutAfterPrint: options.cutAfterPrint,
        dpi: options.dpi || 203,
        rotate: options.rotate || false,
        mirror: options.mirror || false,
        feedEnabled: options.feedEnabled || false,
        feedAmount: options.feedAmount || 5
      });
    } catch (e) {
      console.error('Print Image failed', e);
      throw e;
    }
  } else {
    throw new Error('printImage is only supported on Android/iOS');
  }
};

// --- OKI LASER PRINTER FUNCTIONS ---

/**
 * Discover OKI printers connected via USB
 */
export const discoverOkiPrinters = async (): Promise<{ printers: any; count: number }> => {
  if (!isAndroid) {
    return { printers: {}, count: 0 };
  }
  return await OkiPrinter.discoverPrinters();
};

/**
 * Connect to OKI printer via USB (auto-detects first OKI device by vendor ID 0x06BC)
 */
export const connectOkiPrinter = async (): Promise<{ success: boolean; message: string; deviceName?: string; productName?: string }> => {
  if (!isAndroid) {
    throw new Error('OKI printer connection is only supported on Android');
  }
  return await OkiPrinter.connect();
};

/**
 * Connect to OKI printer via network (IP address)
 */
export const connectOkiPrinterNetwork = async (ipAddress: string, port?: number): Promise<{ success: boolean; message: string }> => {
  if (!isMobile) {
    throw new Error('OKI printer connection is only supported on Android/iOS');
  }
  return await OkiPrinter.connectNetwork({ ipAddress, port: port || 9100 });
};

/**
 * Disconnect from OKI printer
 */
export const disconnectOkiPrinter = async (): Promise<{ success: boolean; message: string }> => {
  if (!isMobile) {
    return { success: true, message: 'Not on mobile' };
  }
  return await OkiPrinter.disconnect();
};

/**
 * Get OKI printer connection status
 */
export const getOkiPrinterStatus = async (): Promise<{ connected: boolean; deviceName?: string; productName?: string }> => {
  if (!isMobile) {
    return { connected: false };
  }
  return await OkiPrinter.getStatus();
};

/**
 * Print PDF to OKI laser printer via USB
 * The PDF is wrapped in a PJL job with configurable variables
 * @param base64Pdf - Base64 encoded PDF data
 * @param options - OKI PJL configuration options
 */
export const printToOki = async (
  base64Pdf: string,
  options?: {
    mediaSource?: 'AUTO' | 'TRAY1' | 'TRAY2' | 'MPTRAY' | 'MANUALFEED';
    mediaWeight?: 'LIGHT' | 'MEDIUM' | 'HEAVY' | 'ULTRAHEAVY';
    mediaType?: 'PLAIN' | 'LABELS' | 'BOND' | 'RECYCLED' | 'CARDSTOCK' | 'ROUGH';
    paperSize?: 'FLEXI' | 'A4' | 'OBLONG' | 'OVERSIZE' | '4X4_SQUARE' | 'JDM_IMPORT' | 'MOTO_9X7' | 'MOTO_MID' | 'MOTO_COMPACT' | 'MOTO_MINI' | 'CUSTOM';
    customPaperWidth?: number; // in mm - converted to decipoints by native plugin
    customPaperLength?: number; // in mm - converted to decipoints by native plugin
    resolution?: 600 | 1200;
    economode?: boolean;
    density?: 1 | 2 | 3 | 4 | 5;
    orientation?: 'PORTRAIT' | 'LANDSCAPE';
    xOffset?: number;
    yOffset?: number;
    leftMarginCompensation?: number;  // in mm, negative shifts left (default 0)
    customPjlHeader?: string;  // Custom PJL header - use {WIDTH} and {HEIGHT} as placeholders
  }
): Promise<{ success: boolean; message: string; bytesSent?: number; pjlHeader?: string; prnFile?: string }> => {
  if (!isMobile) {
    throw new Error('OKI printing is only supported on Android/iOS');
  }

  // Clean base64 if needed (remove data URI prefix)
  const base64Data = base64Pdf.includes(',')
    ? base64Pdf.split(',')[1]
    : base64Pdf;

  // Apply defaults here since Java plugin is now strict/minimal
  // Default paper size to OBLONG (standard UK number plate) instead of A4
  return await OkiPrinter.printRawPdf({
    base64Pdf: base64Data,
    mediaSource: options?.mediaSource ?? 'MULTI',
    mediaWeight: options?.mediaWeight ?? 'ULTRAHEAVY',
    mediaType: options?.mediaType ?? 'LABELS',
    paperSize: options?.paperSize ?? 'OBLONG',
    customPaperWidth: options?.customPaperWidth,
    customPaperLength: options?.customPaperLength,
    resolution: options?.resolution ?? 600,
    economode: options?.economode ?? false,
    density: options?.density ?? 5,
    orientation: options?.orientation ?? 'PORTRAIT',
    xOffset: options?.xOffset,
    yOffset: options?.yOffset,
    leftMarginCompensation: options?.leftMarginCompensation ?? 0,  // Default 0 - no compensation
    customPjlHeader: options?.customPjlHeader
  });
};

/**
 * Direct access to printRawPdf without defaults (for debugging)
 */
export const printToOkiRaw = async (options: any): Promise<{ success: boolean; message: string; bytesSent?: number; pjlHeader?: string }> => {
    if (!isMobile) throw new Error('Android/iOS only');
    
    // Clean base64 if needed
    if (options.base64Pdf && options.base64Pdf.includes(',')) {
        options.base64Pdf = options.base64Pdf.split(',')[1];
    }
    
    return await OkiPrinter.printRawPdf(options);
};

/**
 * Print raw bytes to OKI printer (for testing or custom jobs)
 */
export const printRawToOki = async (base64Data: string): Promise<{ success: boolean; bytesSent?: number }> => {
  if (!isMobile) {
    throw new Error('OKI printing is only supported on Android/iOS');
  }
  return await OkiPrinter.printRawBytes({ base64Data });
};

/**
 * Run diagnostic test print with different driver modes
 */
export const testOkiPrint = async (options: string | any): Promise<{ success: boolean; message: string }> => {
  if (!isMobile) {
    throw new Error('OKI testing is only supported on Android/iOS');
  }

  if (typeof options === 'string') {
      return await OkiPrinter.testPrint({ mode: options });
  } else {
      return await OkiPrinter.testPrint(options);
  }
};

// --- CITIZEN LABEL PRINTER FUNCTIONS ---

/**
 * Connect to Citizen USB printer (shows USB permission dialog if needed)
 */
export const connectCitizenPrinter = async (): Promise<{ success: boolean; message: string; type: 'usb' }> => {
  if (!isAndroid) {
    throw new Error('Citizen printer connection is only supported on Android');
  }
  return await CitizenPrinter.connectUSB();
};

/**
 * Disconnect from Citizen printer
 */
export const disconnectCitizenPrinter = async (): Promise<{ success: boolean; message: string }> => {
  if (!isAndroid) {
    return { success: true, message: 'Not on Android' };
  }
  return await CitizenPrinter.disconnect();
};

/**
 * Get Citizen printer connection status
 */
export const getCitizenPrinterStatus = async (): Promise<{ connected: boolean; type?: string; driverType?: string }> => {
  if (!isAndroid) {
    return { connected: false };
  }
  return await CitizenPrinter.getConnectionStatus();
};

/**
 * Print PDF to Citizen label printer via USB
 * @param base64Pdf - Base64 encoded PDF data
 * @param options - Print options
 */
export const printToCitizen = async (
  base64Pdf: string,
  options?: {
    widthMm?: number;
    heightMm?: number;
    copies?: number;
  }
): Promise<{ success: boolean; message: string; copies?: number }> => {
  if (!isAndroid) {
    throw new Error('Citizen printing is only supported on Android');
  }

  // Clean base64 if needed (remove data URI prefix)
  const base64Data = base64Pdf.includes(',')
    ? base64Pdf.split(',')[1]
    : base64Pdf;

  return await CitizenPrinter.printPdf({
    pdfData: base64Data,
    widthMm: options?.widthMm,
    heightMm: options?.heightMm,
    copies: options?.copies ?? 1
  });
};

/**
 * Send raw TSPL commands to Citizen printer
 */
export const sendCitizenTspl = async (commands: string): Promise<{ success: boolean; message: string }> => {
  if (!isAndroid) {
    throw new Error('Citizen TSPL is only supported on Android');
  }
  return await CitizenPrinter.printTspl({ commands });
};
